import { usePager, Pager } from "@/components/kiyana/Pager";

import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { formatIDR, formatDateID } from "@/lib/format";
import { ChevronLeft, Package } from "lucide-react";

const TABS = [
  { id: "all", name: "Semua" },
  { id: "belum_bayar", name: "Belum Bayar" },
  { id: "diproses", name: "Diproses" },
  { id: "dikirim", name: "Dikirim" },
  { id: "selesai", name: "Selesai" },
  { id: "retur", name: "Retur" },
  { id: "batal", name: "Batal" },
];

const UNPAID_PS_STORE = ["menunggu_pembayaran_manual", "menunggu_pembayaran_xendit", "bukti_ditolak", "menunggu_verifikasi_manual", "cod_menunggu_verifikasi", "cod_ditolak"];

export const matchTab = (o, tab) => {
  const ps = o.payment_status, os = o.order_status;
  if (tab === "belum_bayar") return UNPAID_PS_STORE.includes(ps) && os !== "dibatalkan";
  if (tab === "diproses") return ["sudah_dibayar", "cod_belum_terbayar"].includes(ps) && ["pembayaran_diverifikasi", "perlu_diproses", "diproses", "dikemas", "siap_dikirim"].includes(os);
  if (tab === "dikirim") return os === "dikirim";
  if (tab === "selesai") return ["diterima", "selesai"].includes(os);
  if (tab === "retur") return (os || "").startsWith("retur") || (os || "").startsWith("refund");
  if (tab === "batal") return os === "dibatalkan";
  // tab "Semua": status belum bayar hanya tampil di menu Belum Bayar
  return !(UNPAID_PS_STORE.includes(ps) && os !== "dibatalkan");
};

const STATUS_LABEL = (o) => {
  if (o.payment_status === "bukti_ditolak") return "Bukti Ditolak";
  if (o.payment_status === "cod_menunggu_verifikasi") return "Verifikasi COD";
  if (o.payment_status === "cod_ditolak") return "COD Ditolak";
  if (o.payment_status === "cod_ditolak_pembeli") return "Paket COD Ditolak";
  if (o.payment_status === "menunggu_verifikasi_manual") return "Verifikasi Pembayaran";
  if (["menunggu_pembayaran_manual", "menunggu_pembayaran_xendit"].includes(o.payment_status)) return "Belum Bayar";
  return (o.order_status || "").replace(/_/g, " ");
};

export default function StoreOrders() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const [orders, setOrders] = useState(null);
  const tab = sp.get("status") || "all";

  useEffect(() => {
    if (user) api.get("/orders").then((r) => setOrders(r.data)).catch(() => setOrders([]));
  }, [user]);

  const filtered = (orders || []).filter((o) => matchTab(o, tab));
  const { paged: pagedOrders, pager: soPager } = usePager(filtered, 10);

  if (!user) {
    return (
      <div className="text-center py-16">
        <Package className="w-12 h-12 text-cocoa-muted mx-auto mb-3" />
        <div className="text-sm text-cocoa-muted mb-4">Masuk untuk melihat pesananmu</div>
        <Link to="/store/auth" className="px-6 h-11 leading-[44px] inline-block rounded-full bg-brand text-white text-sm font-semibold">Masuk</Link>
      </div>
    );
  }

  return (
    <div className="space-y-3 animate-fade-up">
      <div className="flex items-center justify-between">
        <button onClick={() => nav(-1)} className="w-9 h-9 rounded-full bg-white border border-blush-soft flex items-center justify-center">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <h1 className="font-semibold">Pesanan Saya</h1>
        <div className="w-9" />
      </div>

      <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-4 px-4">
        {TABS.map((t) => (
          <button
            key={t.id}
            data-testid={`orders-tab-${t.id}`}
            onClick={() => setSp({ status: t.id })}
            className={`px-4 h-8 flex-shrink-0 rounded-full text-xs font-medium border ${tab === t.id ? "bg-brand text-white border-brand" : "bg-white border-blush-soft text-cocoa"}`}
          >{t.name}</button>
        ))}
      </div>

      {orders === null && <div className="text-center py-10 text-sm text-cocoa-muted">Memuat...</div>}
      {orders !== null && filtered.length === 0 && (
        <div className="text-center py-14 bg-white rounded-2xl border border-blush-soft">
          <Package className="w-10 h-10 text-cocoa-muted mx-auto mb-2" />
          <div className="text-sm text-cocoa-muted">Belum ada pesanan di status ini.</div>
        </div>
      )}
      {pagedOrders.map((o) => (
        <Link key={o.id} to={`/store/order/${o.id}`} data-testid={`order-card-${o.code}`} className="block bg-white rounded-2xl p-4 border border-blush-soft">
          <div className="flex justify-between items-start mb-2">
            <div>
              <div className="text-[10px] text-cocoa-muted">{formatDateID(o.created_at)}</div>
              <div className="font-semibold text-sm">#{o.code}</div>
            </div>
            <div className="flex flex-col items-end gap-1">
              <span className={`text-[10px] rounded-full px-2 py-0.5 capitalize ${
                ["sudah_dibayar", "cod_belum_terbayar"].includes(o.payment_status) ? "bg-emerald-50 text-emerald-600" :
                ["bukti_ditolak", "cod_ditolak", "cod_ditolak_pembeli"].includes(o.payment_status) ? "bg-red-50 text-red-600" : "bg-blush text-brand"
              }`}>{STATUS_LABEL(o)}</span>
              {o.cancel_request?.status === "menunggu_persetujuan" && (
                <span data-testid={`cancel-badge-${o.code}`} className="text-[10px] rounded-full px-2 py-0.5 bg-orange-50 text-orange-600">Pembatalan Diajukan</span>
              )}
            </div>
          </div>
          <div className="flex gap-2 items-center">
            {o.items.slice(0, 3).map((it, i) => (
              <img key={i} src={it.image} alt="" className="w-11 h-11 rounded-lg object-cover border border-blush-soft" />
            ))}
            <div className="text-[11px] text-cocoa-muted ml-1">{o.items.length} produk</div>
          </div>
          <div className="flex justify-between items-center pt-2.5 mt-2.5 border-t border-blush-soft">
            <span className="text-[11px] text-cocoa-muted">Total Belanja</span>
            <span className="text-brand font-bold text-sm">{formatIDR(o.total)}</span>
          </div>
          {["menunggu_pembayaran_manual", "menunggu_pembayaran_xendit", "bukti_ditolak"].includes(o.payment_status) && (
            <button
              data-testid={`pay-now-${o.code}`}
              onClick={(e) => { e.preventDefault(); nav(`/store/order/${o.id}`); }}
              className="mt-2.5 w-full h-10 rounded-full bg-brand text-white text-xs font-semibold"
            >Bayar Sekarang</button>
          )}
        </Link>
      ))}
      <Pager {...soPager} testid="so-pager" />
    </div>
  );
}
